import React from 'react';
import PropTypes from 'prop-types';
import { ResponsivePie } from '@nivo/pie';
import numeral from 'numeral';

const GenderDemo = ({
  query: { data, loading },
  layout,
  width,
  height,
  topTooltipLabel,
  showSize = true
}) => {
  const colors = {
    female: 'hsl(50, 100%, 50%)',
    male: 'hsl(205, 53%, 47%)'
  };
  const getColor = bar => colors[bar.id];

  const ToolTip = info => {
    const { color, id, value } = info;
    return (
      <div className="tvt-timeline__tooltip">
        <div className="tvt-timeline__tooltip-data">
          <div className="tvt-timeline__tooltip-data-date">
            <div
              className="tvt-timeline__tooltip-data-color"
              style={{ backgroundColor: color }}
            />
            <span className="tvt-timeline__tooltip-data-gender">{id}</span>
          </div>
          <div className="tvt-timeline__tooltip-data-label">
            {topTooltipLabel}:
            <div className="tvt-timeline__tooltip-data-followers">{value}%</div>
          </div>
        </div>
      </div>
    );
  };

  const sampleSize = d =>
    d.reduce((accum, current) => (accum += current.viewers), 0);

  if (layout === 'horizontal') {
    return !loading ? (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {showSize && (
          <div className="tvt-chart__sample-size-num">
            {`N = ${numeral(sampleSize(data)).format(',')}`}
          </div>
        )}
        <ResponsivePie
          data={data}
          width={width}
          height={height}
          margin={{ top: 0, right: 80, bottom: 120, left: 80 }}
          innerRadius={0}
          colors={getColor}
          enableRadialLabels={false}
          enableSlicesLabels={false}
          tooltip={ToolTip}
          theme={{
            tooltip: {
              container: {
                background: '#000'
              }
            }
          }}
          legends={[
            {
              direction: 'column',
              itemHeight: 30,
              translateX: 25,
              translateY: 350,
              itemTextColor: '#999',
              symbolSize: 18,
              symbolShape: 'square'
            }
          ]}
        />
      </div>
    ) : (
      <div className="tvt-time-chart__loading-state-wrapper">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="480"
          height="200"
          viewBox="0 0 480 200"
        >
          <defs>
            <linearGradient id="fnjpg4u7ca" x1="0%" x2="100%" y1="50%" y2="50%">
              <stop offset="0%" stopColor="#EEEEEE" />
              <stop offset="33%" stopColor="#EEEEEE" />
              <stop offset="50%" stopColor="#f5f5f5" />
              <stop offset="67%" stopColor="#EEEEEE" />
              <stop offset="100%" stopColor="#EEEEEE" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                from="-1 0"
                to="1 0"
                begin="0s"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>
          <path
            fill="url(#fnjpg4u7ca)"
            fillRule="evenodd"
            d="M146 79v100l78.84 61.523C206.539 263.943 178.028 279 146 279c-55.228 0-100-44.772-100-100S90.772 79 146 79zm3 0c54.676 0 99 44.324 99 99 0 22.717-7.652 43.647-20.518 60.353L149 177.102V79zm377 110v20H358v-20h168zm-180 0v20h-20v-20h20zm0-40v20h-20v-20h20zm180 0v20H358v-20h168z"
            transform="translate(-46 -79)"
          />
        </svg>
      </div>
    );
  }

  return !loading ? (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div className="tvt-chart__sample-size-num">
        {`N = ${numeral(sampleSize(data)).format(',')}`}
      </div>
      <ResponsivePie
        data={data}
        width={width}
        height={height}
        margin={{ top: 40, right: 200, bottom: 40, left: 0 }}
        innerRadius={0}
        colors={getColor}
        enableRadialLabels={false}
        enableSlicesLabels={false}
        tooltip={ToolTip}
        theme={{
          tooltip: {
            container: {
              background: '#000'
            }
          }
        }}
        legends={[
          {
            direction: 'column',
            itemHeight: 30,
            translateX: 350,
            translateY: 75,
            itemTextColor: '#999',
            symbolSize: 18,
            symbolShape: 'square'
          }
        ]}
      />
    </div>
  ) : (
    <div className="tvt-time-chart__loading-state-wrapper gender-loading-chart">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="480"
        height="200"
        viewBox="0 0 480 200"
      >
        <defs>
          <linearGradient id="fnjpg4u7ca" x1="0%" x2="100%" y1="50%" y2="50%">
            <stop offset="0%" stopColor="#EEEEEE" />
            <stop offset="33%" stopColor="#EEEEEE" />
            <stop offset="50%" stopColor="#f5f5f5" />
            <stop offset="67%" stopColor="#EEEEEE" />
            <stop offset="100%" stopColor="#EEEEEE" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from="-1 0"
              to="1 0"
              begin="0s"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
        <path
          fill="url(#fnjpg4u7ca)"
          fillRule="evenodd"
          d="M146 79v100l78.84 61.523C206.539 263.943 178.028 279 146 279c-55.228 0-100-44.772-100-100S90.772 79 146 79zm3 0c54.676 0 99 44.324 99 99 0 22.717-7.652 43.647-20.518 60.353L149 177.102V79zm377 110v20H358v-20h168zm-180 0v20h-20v-20h20zm0-40v20h-20v-20h20zm180 0v20H358v-20h168z"
          transform="translate(-46 -79)"
        />
      </svg>
    </div>
  );
};

GenderDemo.propTypes = {
  query: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({})),
    loading: PropTypes.bool
  }).isRequired,
  layout: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  topTooltipLabel: PropTypes.string,
  bottomTooltipLabel: PropTypes.string
};

GenderDemo.defaultProps = {
  layout: 'horizontal',
  width: 378,
  height: 464,
  topTooltipLabel: '% of Followers',
  bottomTooltipLabel: 'Followers'
};

export default GenderDemo;
